'use client'

import { useTranslations } from 'next-intl'

import { TRoomDetail, TSimpleReviewCount } from '@/entities/room'
import { useAirbridgeContext } from '@/shared/config'
import { useMultiScrollState } from '@/shared/lib'
import { Button, Icon, Separator } from '@/shared/ui'

import { Heading } from '../common'
import { MobileOtherRooms } from './other-rooms'
import { MobileReview } from './review'
import { SuperHostBadge } from './super-host-badge'
import { MobileUserInformation } from './user-information'
import { TabValue } from '../../lib/tab'

type Props = Pick<TRoomDetail, 'rid' | 'hostUser' | 'otherHostRooms'> & {
  hostSimpleReview: TSimpleReviewCount[]
}

export const MobileHostIntroduction = ({
  rid,
  hostUser,
  otherHostRooms,
  hostSimpleReview,
}: Props) => {
  const t = useTranslations('room_detail')

  const airbridge = useAirbridgeContext()

  const { registerScrollElement } = useMultiScrollState<HTMLElement>()

  const handleChatButtonClick = () => {
    airbridge.openDeeplink({
      deeplinks: {
        ios: `spacev33m2://app?type=room&idx=${rid}`,
        android: `spacev33m2://app?type=room&idx=${rid}`,
        desktop: `${window.location.origin}/guest/room/${rid}`,
      },
      ctaParams: {
        cta_param_1: 'room_detail',
        cta_param_2: 'chat_in_app',
        cta_param_3: '',
      },
    })
  }

  return (
    <section
      className="max-w-screen px-4 py-10"
      ref={element => {
        if (element) {
          registerScrollElement(TabValue.HOST_INFORMATION, element)
        }
      }}
    >
      <Heading size="small" className="mb-4">
        {t('landlord_profile.title')}
      </Heading>

      <div className="rounded-lg bg-white shadow-[0px_1px_7px_0px_rgba(0,0,0,0.15)] md:rounded-sm">
        {hostUser.isSuperHost && (
          <SuperHostBadge
            className="rounded-t-lg md:rounded-t-sm"
            textClassName="typo-caption1 font-semibold md:typo-label2 md:font-medium"
          />
        )}
        <div className="md:flex md:justify-between md:p-10">
          <div className="md:w-[298px] md:space-y-5">
            <MobileUserInformation {...hostUser} />
            <MobileReview hostSimpleReview={hostSimpleReview} />
          </div>
          <Separator color="light-gray" className="md:hidden" />
          <div className="space-y-6 px-5 py-6 md:w-[292px] md:space-y-5 md:p-0">
            <MobileOtherRooms otherHostRooms={otherHostRooms} />
            <Button
              variant="outline"
              shape="pill"
              size="medium"
              fullWidth
              onClick={handleChatButtonClick}
              className="typo-label1"
            >
              {t('landlord_profile.button.chat_in_app')}
              <Icon name="arrow-right" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}
